/** @format **/

import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import PlatformMsisdnPoolNumber from '../../../interfaces/platform/PlatformMsisdnPoolNumber';
import GridContainer from '../../../components/Grid/GridContainer';
import { useDispatch } from 'react-redux';
import GridItem from '../../../components/Grid/GridItem';
import PaperedTable from '../../../components/Table/PaperedTable';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import I18n from '../../../components/Translate/I18n';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import PlkCommand from '../../../modules/block/PlkCommand';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { StorageReducerAction } from '../../../modules/storage/StorageReducers';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import { MsisdnPlkHistoryItem } from './MsisdnPlkHistoryItem';
import { MvnoBlockListCollectionInterface } from '../../../interfaces/collections/MvnoBlockListCollectionInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import ApiDialog from '../../Provisioning/components/ApiDialog';
import { PlusOrdersCommonResponse } from '../../../interfaces/platform/PlusOrdersCommonResponse';
import { PlusOrdersCommonResponseView } from '../../Provisioning/components/PlusOrdersCommonResponseView';
import Datetime from 'react-datetime';
import moment, { Moment } from 'moment';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { FilterHelper } from '../../lib/search/FilterHelper';
import Card from '../../../components/Card/Card';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import CardBody from '../../../components/Card/CardBody';
import OplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
type MsisdnPlkHistoryType = {
    classes: any;
    msisdn: PlatformMsisdnPoolNumber;
    platformResellerCollection: PlatformResellerCollectionInterface;
};

const defaultFiters = (msisdn: PlatformMsisdnPoolNumber): Array<Filter> => {
    const currentPeriod = moment();
    return [
        { name: 'msisdn', value: msisdn.msisdn },
        { name: 'createdAt[after]', value: DateHelperTs.getMonthFirstDaySimple(currentPeriod, 'YYYY-MM-01') },
        { name: 'createdAt[before]', value: DateHelperTs.addMonthsToDate(currentPeriod, 1).format('YYYY-MM-01') },
    ];
};
const defaultOrders = (): Array<Order> => [{ name: 'createdAt', value: 'desc' }];

const MsisdnPlkHistory: FunctionComponent<MsisdnPlkHistoryType> = ({ classes, msisdn, platformResellerCollection }): React.ReactElement => {
    const dispatch = useDispatch();
    const [mvnoBlockList, setMvnoBlockList] = useState<MvnoBlockListCollectionInterface | null>(null);
    const [filters, setFilters] = useState<Array<Filter>>(defaultFiters(msisdn));
    const [orders, setOrders] = useState<Array<Order>>(defaultOrders());
    const [viewDialog, setViewDialog] = useState(false);
    const [viewContent, setViewContent] = useState<any>(null);
    const [viewTitle, setViewTitle] = useState<any>(null);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };

    const updateOrderList = (newOrders: Array<Order>): void => setOrders(newOrders);
    const updateFilterList = (newFilters: Array<Filter>): void => setFilters(newFilters);

    const firstLoad = useCallback((): void => {
        dispatch({
            type: StorageReducerAction.LOAD_STORAGE_HISTORY_PLK_LIST,
            plusOrdersCommonResponseCollection: null,
        });
    }, [dispatch]);

    useEffect(() => {
        firstLoad();
    }, [dispatch, filters, orders, firstLoad]);

    useEffect(() => {
        const itemLoader = new ItemLoaders(dispatch);
        const loadBlockList = (): void => {
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_block_lists')),
                'plus_orders_common_response_block_list',
                [],
                [],
                (data: MvnoBlockListCollectionInterface): void => {
                    setMvnoBlockList(data);
                    dispatch({
                        type: StorageReducerAction.LOAD_STORAGE_HISTORY_PLK_LIST,
                        plusOrdersCommonResponseCollection: null,
                    });
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        };
        if (!mvnoBlockList) {
            loadBlockList();
        }
    }, [dispatch, mvnoBlockList]);

    const viewPlkMessage = (item: PlusOrdersCommonResponse): void => {
        setViewDialog(true);
        setViewContent(<PlusOrdersCommonResponseView item={item} />);
        setViewTitle(<I18n trans={'prov:api.api_plk_method_call'} valueObject={{ function_name: item.command }} />);
    };

    const hideDialog = (): void => {
        setViewDialog(false);
        setViewContent(null);
        setViewTitle(null);
    };

    const updatePeriod = (date: string, fieldName: string): void => {
        let moment1 = moment(date);
        let filterHelper = new FilterHelper({ filters: filters }, { rangeFlag: false });
        let newFilters = filterHelper.updateField(fieldName + '[after]', DateHelperTs.getMonthFirstDaySimple(moment1, 'YYYY-MM-01'));
        if (newFilters === null) {
            return;
        }
        filterHelper = new FilterHelper({ filters: newFilters }, { rangeFlag: false });
        const filter = filterHelper.updateField(fieldName + '[before]', DateHelperTs.addMonthsToDate(moment1, 1).format('YYYY-MM-01'));
        if (filter) {
            setFilters(filter);
        }
    };

    return (
        <Card>
            <CardHeader trans="pools:msisdn.plk_history" />
            <CardBody>
                <PaperedTable>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="none" align="left" colSpan={6} style={{ paddingTop: '20px', paddingBottom: '20px', paddingLeft: 0 }}>
                                <I18n trans="prov:h.search_period" />
                                <Datetime
                                    locale="pl"
                                    viewMode="months"
                                    defaultValue={moment(DateHelperTs.getCurrentDateSimple('YYYY-MM-01'))}
                                    dateFormat="YYYY-MM"
                                    timeFormat={false}
                                    onChange={(moment: Moment | string): void => {
                                        if (typeof moment !== 'string') {
                                            updatePeriod(moment.format('YYYY-MM-01'), 'createdAt');
                                        }
                                    }}
                                    inputProps={{
                                        id: 'createdAt',
                                        placeholder: 'YYYY-MM',
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableControlStickyHeader} align="left" style={{ paddingLeft: 0 }}>
                                <TableHeaderContentCell trans="general:common.commands" />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ paddingLeft: 0 }}>
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:list.id"
                                    fieldName="id"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="desc"
                                    searchFieldType="number"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            {/*{Auth.getUserType() === tokenInfo.SYSTEM_TYPE_MVNO_ADMIN && (*/}
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    title="prov:list.partner"
                                    textAlign="center"
                                    fieldName="platformResellerId"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerPlatformReseller"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            {/*)}*/}
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:list.block_request_id"
                                    fieldName="mvnoBlockRequestId"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="desc"
                                    searchFieldType="number"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    nullable={false}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    title="prov:list.block"
                                    fieldName="mvnoBlockListId"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerMvnoBlockList"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    platformResellerList={platformResellerCollection?.['hydra:member']}
                                    nullable={false}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="prov:list.operation"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    fieldName="command"
                                    showSort={true}
                                    onlyFilter={true}
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={PlkCommand.getList()}
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    title="prov:list.imsi"
                                    fieldName="imsi"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    title="prov:list.request_date"
                                    fieldName="requestTime"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="asc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD HH:MM"
                                    dateFromTo={true}
                                    filters={filters}
                                    sort={orders}
                                    breakLine={true}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    title="prov:list.response_date"
                                    fieldName="responseTime"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="asc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD HH:MM"
                                    dateFromTo={true}
                                    filters={filters}
                                    sort={orders}
                                    breakLine={true}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    nullable={false}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="center">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="prov:list.status"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    fieldName="errorStatus"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerPlkStatus"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {mvnoBlockList && (
                        <MsisdnPlkHistoryItem
                            msisdn={msisdn}
                            filters={filters}
                            orders={orders}
                            platformResellerColection={platformResellerCollection}
                            mvnoBlockList={mvnoBlockList}
                            viewPlkMessage={viewPlkMessage}
                        />
                    )}
                </PaperedTable>
                <ApiDialog open={viewDialog} fullWidth={true} closeCallback={hideDialog} content={viewContent} title={viewTitle} />
            </CardBody>
        </Card>
    );
};

export default withStyles(OplAppStyle)(MsisdnPlkHistory);
